import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { api, mensajeDe, type EstadoRespaldo, type ResumenDelSitio, type TorneoDelSitio } from '../../services/api';
import { Aviso, Boton, Campo, Cargando, ENTRADA } from '../../components/ui';
import { rangoDeFechas } from '../../nucleo/fechas';
import { LARGO_MAXIMO_TEXTO_DE_PAGO, nombreDeTipo, puedePublicar, textoDeTope, validarConfiguracion, validarHabilitacion } from '../../nucleo/habilitacion';
import { ETIQUETA_ESTADO } from './ListaTorneos';
import { Respaldo } from './Respaldo';
import { InvitacionOrganizador } from '../../components/InvitacionOrganizador';
import { ORIGEN_PUBLICO } from '../../nucleo/sitio';

// El panel del dueño del sitio (etapa 7): lo único que ve por encima de los
// clubes. Acá se dan de alta los clubes, se habilitan los torneos al cobrar, y
// se fijan el tope de la prueba gratis y el texto de cómo pagar.

export function Sitio() {
  const [datos, setDatos] = useState<ResumenDelSitio | null>(null);
  const [error, setError] = useState('');
  const [respaldo, setRespaldo] = useState<EstadoRespaldo | null>(null);

  const recargar = useCallback(async () => {
    try {
      setDatos(await api.resumenDelSitio());
      setError('');
    } catch (e) {
      setError(mensajeDe(e));
    }
  }, []);

  useEffect(() => {
    void recargar();
  }, [recargar]);

  if (!datos) return error ? <Aviso tono="error">{error}</Aviso> : <Cargando />;

  const tope = datos.configuracion.prueba_tope_parejas;
  const esperando = datos.clubes.flatMap((c) => c.torneos.filter((t) =>
    t.estado === 'borrador' && t.parejas > 0 && !puedePublicar(t.parejas, t.habilitacion, pruebaDisponible(c, tope), limitePrueba(c, tope)).ok,
  ).map((t) => ({ club: c, t })));
  const totalTorneos = datos.clubes.reduce((n, c) => n + c.torneos.length, 0);
  const alertaRespaldo = Boolean(respaldo && (!respaldo.configurado || respaldo.atrasado || respaldo.ultimo === null));

  return (
    <>
      <header>
        <h1 className="font-titulo text-4xl uppercase">Administrar el sitio</h1>
        <p className="text-sm text-(--color-apagado)">
          Clubes, permisos para publicar y configuración. Solo vos, como dueño, tenés acceso.
        </p>
        <p className="mt-2 text-sm tabular-nums text-(--color-acento)">
          {datos.clubes.length} club{datos.clubes.length === 1 ? '' : 'es'} · {totalTorneos} torneo{totalTorneos === 1 ? '' : 's'}
        </p>
      </header>
      {error && <Aviso tono="error">{error}</Aviso>}

      <SeccionSitio id="habilitaciones" titulo="Para habilitar" detalle={esperando.length ? `${esperando.length} torneo${esperando.length === 1 ? ' necesita' : 's necesitan'} tu intervención` : 'Todos los torneos con parejas tienen una opción para publicar'} cantidad={esperando.length} alerta={esperando.length > 0} inicialmenteAbierta={esperando.length > 0}>
        {esperando.length ? (
          <>
            <p className="mb-3 text-sm text-(--color-apagado)">Habilitá el torneo como pago o regalo, o entrá en Clubes para ofrecer una prueba gratis.</p>
            <TorneosDelSitio torneos={esperando.map(({ club, t }) => ({ torneo: t, club: club.nombre, tienePrueba: pruebaDisponible(club, tope) && t.parejas <= limitePrueba(club, tope) }))} recargar={recargar} />
          </>
        ) : <p className="text-sm text-(--color-apagado)">No hay torneos esperando habilitación.</p>}
      </SeccionSitio>

      <SeccionSitio id="clubes" titulo="Clubes" detalle="Buscá un club y desplegá sus organizadores, torneos y prueba gratis" cantidad={datos.clubes.length}>
        <Clubes clubes={datos.clubes} tope={tope} recargar={recargar} />
      </SeccionSitio>
      <SeccionSitio id="nuevo-club" titulo="Nuevo club" detalle="Creá el acceso de un club y su primer organizador">
        <NuevoClub recargar={recargar} />
      </SeccionSitio>
      <SeccionSitio id="configuracion" titulo="Prueba gratis y cobro" detalle="Valor sugerido para nuevas pruebas y cómo se paga. Cada club conserva su límite.">
        <Configuracion inicial={datos.configuracion} recargar={recargar} />
      </SeccionSitio>
      <SeccionSitio id="respaldo" titulo="Respaldo" detalle={alertaRespaldo ? 'El respaldo requiere atención' : 'Copia semanal de los datos de todos los clubes'} alerta={alertaRespaldo}>
        <Respaldo alCambiar={setRespaldo} />
      </SeccionSitio>
    </>
  );
}

function SeccionSitio({ id, titulo, detalle, cantidad, alerta, inicialmenteAbierta = false, children }: {
  id: string; titulo: string; detalle: string; cantidad?: number; alerta?: boolean; inicialmenteAbierta?: boolean; children: ReactNode;
}) {
  const [abierta, setAbierta] = useState(() => {
    try {
      const guardada = localStorage.getItem(`torneos.sitio.${id}`);
      return guardada === null ? inicialmenteAbierta : guardada === 'abierta';
    } catch { return inicialmenteAbierta; }
  });
  return (
    <details open={abierta} onToggle={(e) => {
      const valor = e.currentTarget.open;
      setAbierta(valor);
      try { localStorage.setItem(`torneos.sitio.${id}`, valor ? 'abierta' : 'cerrada'); } catch { /* sin memoria */ }
    }} className={`superficie-tarjeta group/seccion overflow-hidden rounded-2xl border bg-(--color-tarjeta) ${alerta ? 'border-(--color-alerta)' : 'border-(--color-borde)'}`}>
      <summary className="flex min-h-20 cursor-pointer list-none items-center gap-3 px-4 py-4 transition hover:bg-(--color-panel) focus-visible:outline-2 focus-visible:outline-(--color-acento) focus-visible:-outline-offset-2 [&::-webkit-details-marker]:hidden">
        <div className="min-w-0 flex-1">
          <h2 className="flex items-center gap-3 font-titulo text-2xl uppercase">
            {titulo}
            {cantidad !== undefined && <span className={`font-sans text-sm tabular-nums ${alerta ? 'text-(--color-alerta)' : 'text-(--color-acento)'}`}>{cantidad}</span>}
          </h2>
          <p className={`mt-1 text-sm ${alerta ? 'text-(--color-alerta)' : 'text-(--color-apagado)'}`}>{detalle}</p>
        </div>
        <Flecha className="group-open/seccion:rotate-180" />
      </summary>
      <div className="border-t border-(--color-borde) p-4">{children}</div>
    </details>
  );
}

function Flecha({ className = '' }: { className?: string }) {
  return <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={`h-5 w-5 shrink-0 text-(--color-apagado) transition-transform ${className}`}><path d="m6 9 6 6 6-6" /></svg>;
}

type ClubDelSitio = ResumenDelSitio['clubes'][number];
const CLUBES_POR_PAGINA = 10;
const claveBusqueda = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase().trim();
const limitePrueba = (c: ClubDelSitio, sugerido: number) => c.prueba_tope_parejas ?? sugerido;
const pruebaDisponible = (c: ClubDelSitio, tope: number) => c.prueba_habilitada && !c.prueba_usada && limitePrueba(c, tope) > 0;

function textoPrueba(c: ClubDelSitio, tope: number): string {
  if (c.prueba_usada) return 'Prueba usada';
  return c.prueba_habilitada ? `Prueba disponible · hasta ${limitePrueba(c, tope)} parejas` : 'Prueba sin habilitar';
}

function Clubes({ clubes, tope, recargar }: { clubes: ClubDelSitio[]; tope: number; recargar: () => Promise<void> }) {
  const [busqueda, setBusqueda] = useState('');
  const [pagina, setPagina] = useState(0);
  const clave = claveBusqueda(busqueda);
  const encontrados = clubes.filter((c) => claveBusqueda([c.nombre, ...c.organizadores.map((o) => o.nombre), ...c.torneos.map((t) => t.nombre)].join(' ')).includes(clave));
  const paginas = Math.ceil(encontrados.length / CLUBES_POR_PAGINA);
  const actual = Math.min(pagina, Math.max(0, paginas - 1));
  return (
    <div className="flex flex-col gap-1.5">
      <Campo etiqueta="Buscar club, organizador o torneo">
        <input type="search" className={ENTRADA} value={busqueda} placeholder="Nombre del club o de la persona" onChange={(e) => { setBusqueda(e.target.value); setPagina(0); }} />
      </Campo>
      <p className="text-xs text-(--color-apagado)" role="status">{encontrados.length} de {clubes.length} clubes · abrí uno para administrarlo</p>
      {encontrados.slice(actual * CLUBES_POR_PAGINA, (actual + 1) * CLUBES_POR_PAGINA).map((c) => (
        <details key={c.id} name="club-del-sitio" className="group/club rounded-xl border border-(--color-borde) bg-(--color-panel)">
          <summary className="flex min-h-16 cursor-pointer list-none items-center gap-3 p-3 transition hover:bg-(--color-tarjeta) focus-visible:outline-2 focus-visible:outline-(--color-acento) [&::-webkit-details-marker]:hidden">
            <div className="min-w-0 flex-1">
              <h3 className="break-words font-semibold">{c.nombre}</h3>
              <p className="text-xs text-(--color-apagado)">{c.torneos.length} torneo{c.torneos.length === 1 ? '' : 's'} · {textoPrueba(c, tope)}</p>
            </div>
            <Flecha className="group-open/club:rotate-180" />
          </summary>
          <div className="flex flex-col gap-2 border-t border-(--color-borde) p-3">
            <div>
              <h4 className="mb-1 text-sm font-semibold">Organizadores</h4>
              <ul className="flex flex-col gap-3 text-sm">{c.organizadores.map((o) => <AccesoOrganizador key={o.id} club={c} organizador={o} />)}</ul>
            </div>
            <PruebaDelClub club={c} tope={tope} recargar={recargar} />
            <div>
              <h4 className="mb-1 text-sm font-semibold">Torneos</h4>
              {c.torneos.length ? <TorneosDelSitio torneos={c.torneos.map((t) => ({ torneo: t, tienePrueba: pruebaDisponible(c, tope) && t.parejas <= limitePrueba(c, tope) }))} recargar={recargar} /> : <p className="text-sm text-(--color-apagado)">Todavía no armó ningún torneo.</p>}
            </div>
          </div>
        </details>
      ))}
      {!encontrados.length && <p className="py-4 text-sm text-(--color-apagado)">{clubes.length ? 'No hay clubes que coincidan con esa búsqueda.' : 'Todavía no hay clubes. Creá el primero en Nuevo club.'}</p>}
      {paginas > 1 && <nav aria-label="Páginas de clubes" className="flex items-center justify-between gap-2">
        <Boton chico tipo="secundario" deshabilitado={actual === 0} onClick={() => setPagina(actual - 1)}>Anterior</Boton>
        <span className="text-sm tabular-nums text-(--color-apagado)">{actual + 1} / {paginas}</span>
        <Boton chico tipo="secundario" deshabilitado={actual + 1 >= paginas} onClick={() => setPagina(actual + 1)}>Siguiente</Boton>
      </nav>}
    </div>
  );
}

function AccesoOrganizador({ club, organizador: o }: { club: ClubDelSitio; organizador: ClubDelSitio['organizadores'][number] }) {
  const [pin, setPin] = useState('');
  const [generando, setGenerando] = useState(false);
  const [error, setError] = useState('');
  const generar = async () => {
    setGenerando(true);
    setError('');
    try { setPin((await api.invitarOrganizador(club.id, o.id)).pin_provisorio); }
    catch (e) { setError(mensajeDe(e)); }
    finally { setGenerando(false); }
  };
  return <li>
    <p className="text-(--color-apagado)">{o.nombre}{o.es_dueno ? ' · dueño del sitio' : ''}{!o.activo ? ' · desactivado' : o.pin_provisorio ? ' · todavía no entró' : ''}</p>
    {!!o.activo && !!o.pin_provisorio && !o.es_dueno && (pin
      ? <InvitacionOrganizador club={club.nombre} nombre={o.nombre} pin={pin} />
      : <div className="mt-2">
        <Boton chico deshabilitado={generando} onClick={() => void generar()}>{generando ? 'Preparando...' : 'Enviar invitación'}</Boton>
        <p className="mt-1 text-xs text-(--color-apagado)">Genera un PIN nuevo y reemplaza cualquier invitación anterior.</p>
      </div>)}
    {error && <Aviso tono="error">{error}</Aviso>}
  </li>;
}

function PruebaDelClub({ club, tope, recargar }: { club: ClubDelSitio; tope: number; recargar: () => Promise<void> }) {
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const [editando, setEditando] = useState(false);
  const [limite, setLimite] = useState(String(limitePrueba(club, tope) || ''));
  const cambiar = async (habilitada: boolean) => {
    setGuardando(true);
    setError('');
    try { await api.pruebaDelClub(club.id, habilitada, habilitada ? Number(limite) : undefined); setEditando(false); await recargar(); }
    catch (e) { setError(mensajeDe(e)); }
    finally { setGuardando(false); }
  };
  return (
    <section className="rounded-xl border border-(--color-borde) p-3">
      <h4 className="font-semibold">{textoPrueba(club, tope)}</h4>
      <p className="mt-1 text-sm text-(--color-apagado)">
        {club.prueba_usada ? 'Este club ya publicó su torneo de prueba. Para darle otro gratis, habilitá ese torneo como Regalo.'
          : club.prueba_habilitada ? `Este club puede publicar una prueba de hasta ${limitePrueba(club, tope)} parejas, una sola vez. Los cambios de otros clubes o del valor sugerido no modifican esta oferta.`
          : 'Elegí cuántas parejas puede tener el torneo de prueba de este club. El límite se guarda para este club.'}
      </p>
      {!club.prueba_usada && (editando ? (
        <form className="mt-3 flex flex-col gap-3" onSubmit={(e) => { e.preventDefault(); void cambiar(true); }}>
          <Campo etiqueta="Máximo de parejas para este club" ayuda="De 2 a 500 parejas, sumando todas las categorías del torneo.">
            <input className={ENTRADA} inputMode="numeric" value={limite} onChange={(e) => setLimite(e.target.value.replace(/\D/g, '').slice(0, 3))} />
          </Campo>
          <div className="flex flex-wrap gap-2">
            <Boton chico type="submit" deshabilitado={guardando}>{guardando ? 'Guardando...' : 'Guardar prueba gratis'}</Boton>
            <Boton chico tipo="secundario" deshabilitado={guardando} onClick={() => { setEditando(false); setError(''); }}>Cancelar</Boton>
          </div>
        </form>
      ) : <div className="mt-3 flex flex-wrap gap-2">
        <Boton chico tipo={club.prueba_habilitada ? 'secundario' : 'principal'} deshabilitado={guardando} onClick={() => { setLimite(String(limitePrueba(club, tope) || '')); setEditando(true); setError(''); }}>{club.prueba_habilitada ? 'Cambiar límite de este club' : 'Habilitar prueba gratis'}</Boton>
        {club.prueba_habilitada && <Boton chico tipo="texto" deshabilitado={guardando} onClick={() => { void cambiar(false); }}>{guardando ? 'Guardando...' : 'Retirar prueba disponible'}</Boton>}
      </div>)}
      {error && <div className="mt-2"><Aviso tono="error">{error}</Aviso></div>}
    </section>
  );
}

// ── Un torneo, con su habilitación ────────────────────────────────────────

function TorneosDelSitio({ torneos, recargar }: { torneos: { torneo: TorneoDelSitio; club?: string; tienePrueba: boolean }[]; recargar: () => Promise<void> }) {
  const [pagina, setPagina] = useState(0);
  const paginas = Math.ceil(torneos.length / 5);
  const actual = Math.min(pagina, Math.max(0, paginas - 1));
  return <>
    <ul className="flex flex-col">{torneos.slice(actual * 5, (actual + 1) * 5).map((t) => <FilaTorneo key={t.torneo.id} {...t} recargar={recargar} />)}</ul>
    {paginas > 1 && <nav aria-label="Páginas de torneos" className="mt-3 flex items-center justify-between gap-2">
      <Boton chico tipo="secundario" deshabilitado={actual === 0} onClick={() => setPagina(actual - 1)}>Anterior</Boton>
      <span className="text-sm tabular-nums text-(--color-apagado)">{actual + 1} / {paginas}</span>
      <Boton chico tipo="secundario" deshabilitado={actual + 1 >= paginas} onClick={() => setPagina(actual + 1)}>Siguiente</Boton>
    </nav>}
  </>;
}

function FilaTorneo({ torneo: t, club, recargar, tienePrueba }: { torneo: TorneoDelSitio; club?: string; recargar: () => Promise<void>; tienePrueba: boolean }) {
  const [abierto, setAbierto] = useState(false);
  const [tipo, setTipo] = useState<'pago' | 'regalo'>(t.habilitacion?.tipo === 'regalo' ? 'regalo' : 'pago');
  const [tope, setTope] = useState(t.habilitacion?.tope_parejas?.toString() ?? '');
  const [nota, setNota] = useState(t.habilitacion?.nota ?? '');
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);

  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    const h = validarHabilitacion({ tipo, tope_parejas: tope ? Number(tope) : null, nota });
    if (!h.ok) return setError(h.error);
    setGuardando(true);
    try {
      await api.habilitarTorneo(t.id, h.valor);
      setAbierto(false);
      setError('');
      await recargar();
    } catch (err) {
      setError(mensajeDe(err));
    } finally {
      setGuardando(false);
    }
  };

  const estado = t.habilitacion
    ? `Habilitado (${nombreDeTipo(t.habilitacion.tipo)}), ${textoDeTope(t.habilitacion.tope_parejas)}${t.habilitacion.nota ? ` · ${t.habilitacion.nota}` : ''}`
    : tienePrueba ? 'Puede publicar con la prueba gratis del club' : 'Necesita habilitación';
  const pasado = t.habilitacion?.tope_parejas != null && t.parejas > t.habilitacion.tope_parejas;

  return (
    <li className="border-t border-(--color-borde) py-2.5 first:border-t-0">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="font-semibold">
            {t.nombre}
            {club && <span className="font-normal text-(--color-apagado)"> · {club}</span>}
          </p>
          <p className="text-sm text-(--color-apagado)">
            {rangoDeFechas(t.fecha_desde, t.fecha_hasta)} · {t.parejas} pareja{t.parejas === 1 ? '' : 's'}
          </p>
          <p className={`text-sm ${t.habilitacion || tienePrueba ? 'text-(--color-acento)' : 'text-(--color-alerta)'}`}>
            {estado}
            {pasado && ' — tiene más parejas que el tope'}
          </p>
        </div>
        <span className={`shrink-0 rounded-full border px-2 py-0.5 text-xs font-semibold ${ETIQUETA_ESTADO[t.estado].clase}`}>{ETIQUETA_ESTADO[t.estado].texto}</span>
      </div>

      {!abierto ? (
        <div className="mt-1 flex flex-wrap gap-4 text-sm font-semibold">
          <button className="min-h-11 text-(--color-acento)" onClick={() => { setTipo(t.habilitacion?.tipo === 'regalo' ? 'regalo' : 'pago'); setTope(t.habilitacion?.tope_parejas?.toString() ?? ''); setNota(t.habilitacion?.nota ?? ''); setError(''); setAbierto(true); }}>
            {t.habilitacion ? 'Cambiar habilitación' : 'Habilitar'}
          </button>
          {t.estado !== 'borrador' && (
            <a className="flex min-h-11 items-center text-(--color-apagado)" href={`/t/${t.slug}`} target="_blank" rel="noopener">
              Ver ↗
            </a>
          )}
        </div>
      ) : (
        <form onSubmit={enviar} className="mt-2 flex flex-col gap-2 rounded-xl border border-(--color-acento) p-3">
          <div className="grid grid-cols-2 gap-2">
            {(['pago', 'regalo'] as const).map((x) => (
              <button
                key={x}
                type="button"
                aria-pressed={tipo === x}
                onClick={() => setTipo(x)}
                className={`min-h-10 rounded-xl border text-sm font-semibold ${tipo === x ? 'border-(--color-acento) text-(--color-acento)' : 'border-(--color-borde) text-(--color-apagado)'}`}
              >
                {x === 'pago' ? 'Pagó' : 'Regalo'}
              </button>
            ))}
          </div>
          <p className="text-sm text-(--color-apagado)">Elegí Regalo para darle este torneo gratis, incluso si supera el límite de la prueba.</p>
          <Campo etiqueta="Hasta cuántas parejas" ayuda={`Vacío = sin tope. Hoy tiene ${t.parejas} parejas.`}>
            <input className={ENTRADA} inputMode="numeric" value={tope} placeholder="sin tope" onChange={(e) => setTope(e.target.value.replace(/\D/g, '').slice(0, 3))} />
          </Campo>
          <Campo etiqueta="Nota para vos (opcional)">
            <input className={ENTRADA} value={nota} maxLength={120} placeholder="transferencia del 3/10" onChange={(e) => setNota(e.target.value)} />
          </Campo>
          {error && <Aviso tono="error">{error}</Aviso>}
          <p className="text-xs text-(--color-apagado)">Habilitar es para siempre: lo publicado no se apaga. Se puede ampliar el tope después.</p>
          <div className="flex gap-2">
            <Boton chico type="submit" deshabilitado={guardando}>{guardando ? 'Guardando...' : 'Guardar habilitación'}</Boton>
            <Boton chico tipo="secundario" deshabilitado={guardando} onClick={() => setAbierto(false)}>Cancelar</Boton>
          </div>
        </form>
      )}
    </li>
  );
}

// ── Alta de un club ───────────────────────────────────────────────────────

function NuevoClub({ recargar }: { recargar: () => Promise<void> }) {
  const [club, setClub] = useState('');
  const [organizador, setOrganizador] = useState('');
  const [error, setError] = useState('');
  // El PIN provisorio se muestra UNA vez: la base solo guarda el hash.
  const [creado, setCreado] = useState<{ club: string; organizador: string; pin: string } | null>(null);
  const [guardando, setGuardando] = useState(false);

  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setGuardando(true);
    try {
      const r = await api.crearClub(club, organizador);
      setCreado({ club: r.club.nombre, organizador: r.organizador.nombre, pin: r.pin_provisorio });
      setClub('');
      setOrganizador('');
      await recargar();
    } catch (err) {
      setError(mensajeDe(err));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div>
      <p className="mb-3 text-sm text-(--color-apagado)">El club recibe acceso con un PIN provisorio. Después podés ofrecerle una prueba desde Clubes.</p>
      {creado && (
        <div className="mb-3">
          <Aviso tono="bien">
            Listo: {creado.club}. Pasale esto a {creado.organizador}: entra en <b>{ORIGEN_PUBLICO}/admin</b> con el nombre{' '}
            <b>{creado.organizador}</b> y el PIN <b className="font-mono text-lg tracking-widest">{creado.pin}</b>. El PIN se ve una sola
            vez; al entrar va a elegir uno propio.
          </Aviso>
          <InvitacionOrganizador club={creado.club} nombre={creado.organizador} pin={creado.pin} />
        </div>
      )}
      <form onSubmit={enviar} className="flex flex-col gap-3">
        <Campo etiqueta="Club u organización">
          <input className={ENTRADA} value={club} onChange={(e) => setClub(e.target.value)} placeholder="Club Las Chicas" />
        </Campo>
        <Campo etiqueta="Nombre del organizador" ayuda="Con ese nombre va a entrar. Tiene que ser único en todo el sitio: si hace falta, con apellido.">
          <input className={ENTRADA} value={organizador} onChange={(e) => setOrganizador(e.target.value)} placeholder="Ana López" />
        </Campo>
        {error && <Aviso tono="error">{error}</Aviso>}
        <div>
          <Boton type="submit" deshabilitado={guardando || club.trim().length < 2 || organizador.trim().length < 2}>
            {guardando ? 'Creando...' : 'Dar de alta'}
          </Boton>
        </div>
      </form>
    </div>
  );
}

// ── La prueba gratis y cómo se paga ───────────────────────────────────────

function Configuracion({ inicial, recargar }: { inicial: ResumenDelSitio['configuracion']; recargar: () => Promise<void> }) {
  const [tope, setTope] = useState(String(inicial.prueba_tope_parejas));
  const [texto, setTexto] = useState(inicial.texto_de_pago);
  const [minPago, setMinPago] = useState(String(inicial.minutos_pago));
  const [minPrueba, setMinPrueba] = useState(String(inicial.minutos_prueba));
  const [error, setError] = useState('');
  const [listo, setListo] = useState(false);
  const [guardando, setGuardando] = useState(false);

  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    setListo(false);
    const c = validarConfiguracion({
      prueba_tope_parejas: tope === '' ? Number.NaN : Number(tope),
      texto_de_pago: texto,
      minutos_pago: Number(minPago),
      minutos_prueba: Number(minPrueba),
    });
    if (!c.ok) return setError(c.error);
    setGuardando(true);
    try {
      await api.configurarSitio(c.valor);
      setError('');
      setListo(true);
      await recargar();
    } catch (err) {
      setError(mensajeDe(err));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div>
      <div className="mb-4">
        <Aviso>El límite de cada prueba se elige en Clubes → club → Habilitar prueba gratis. El valor de abajo solo sugiere una cantidad para nuevas ofertas; cambiarlo no modifica las pruebas ya ofrecidas.</Aviso>
      </div>
      <form onSubmit={enviar} className="flex flex-col gap-3">
        <Campo
          etiqueta="Parejas sugeridas para nuevas pruebas"
          ayuda="Podés elegir otra cantidad al ofrecer la prueba a cada club. 0 = sin sugerencia. No cambia las ofertas existentes."
        >
          <input className={ENTRADA} inputMode="numeric" value={tope} onChange={(e) => setTope(e.target.value.replace(/\D/g, '').slice(0, 3))} />
        </Campo>
        <Campo etiqueta="Qué le dice la app al organizador cuando tiene que pagar" ayuda="El precio, tu alias o link de pago, y cómo avisarte.">
          <textarea className={`${ENTRADA} min-h-28 py-2`} value={texto} maxLength={LARGO_MAXIMO_TEXTO_DE_PAGO} onChange={(e) => setTexto(e.target.value)} />
        </Campo>
        <div className="grid gap-3 sm:grid-cols-2">
          <Campo etiqueta="Torneo habilitado: se actualiza cada (min)">
            <input className={ENTRADA} inputMode="numeric" value={minPago} onChange={(e) => setMinPago(e.target.value.replace(/\D/g, '').slice(0, 3))} />
          </Campo>
          <Campo etiqueta="Torneo de prueba: cada (min)">
            <input className={ENTRADA} inputMode="numeric" value={minPrueba} onChange={(e) => setMinPrueba(e.target.value.replace(/\D/g, '').slice(0, 3))} />
          </Campo>
        </div>
        <p className="-mt-1 text-xs text-(--color-apagado)">
          Cada cuánto se actualiza sola la página del público. El torneo de prueba va más espaciado: tener los resultados al momento es parte de
          lo que se paga. Siempre hay un botón “Actualizar”.
        </p>
        {error && <Aviso tono="error">{error}</Aviso>}
        {listo && !error && <Aviso tono="bien">Guardado.</Aviso>}
        <div>
          <Boton type="submit" deshabilitado={guardando}>{guardando ? 'Guardando...' : 'Guardar configuración'}</Boton>
        </div>
      </form>
    </div>
  );
}
